import React, { useState } from 'react';

// constellation
import { Box, Button } from '@zillow/constellation-10';
import { IconChecklistEmptyFilled, IconMapFilled } from '@zillow/constellation-icons';

// components
import ZestimateSummary from '../ZestimateSummary';
import FilterModule from '../FilterModule';
import CompsCarousel from '../CompsCarousel';
import Map from '../Map';
import ListingCard from '../ListingCard';
import DownloadPDFButton from '../DownloadPDFButton';
import RentRangeChart from '../RentRangeChart';
import ChangePriceModal from '../ChangePriceModal';

// flux
import { useAppSelector } from '../flux/configureStore';

// misc/utils
import { currencyStringToInt } from '../utils/numberUtils';
import { actionsContainerStyles, contentRecipe, spacerRecipe } from './styles';

// types
import type Comparables from '../models/Comparables';

export interface PriceMyRentalComponentProps {
  heading?: React.ReactNode;
}

function getPriceOrZestimate(comparable: Comparables, zestimate: number): number {
  if (comparable.isUsersComp) {
    return zestimate;
  }
  return currencyStringToInt(comparable.bubblePrice || '');
}

const PriceMyRentalComponent: React.FunctionComponent<
  React.PropsWithChildren<React.PropsWithChildren<PriceMyRentalComponentProps>>
> = (props) => {
  const { heading } = props;

  const { comparables, address, app } = useAppSelector((state) => state);
  const { device, googleMapsApiKey } = app;
  const { isMobile } = device;

  const [isMapOpen, setIsMapOpen] = useState(!isMobile);
  const [isLoading, setIsLoading] = useState(false);
  const activeId: string = useAppSelector((state) => state.ui.activeId);
  const activeComp = comparables.items.find((comp: Comparables) => comp.listingId === activeId);
  const priceFromFilter = useAppSelector((state) => state.filter.price);

  const isCompsCarouselOpen = isMobile ? !isMapOpen : true;
  const isListingCardOpen = isMobile && isMapOpen && !activeComp?.isUsersComp;
  const areAppValuesSet = !!googleMapsApiKey && !!device;
  const { rentZestimate } = address;
  const userPropertyPrice = priceFromFilter || rentZestimate;

  const handleMapOrListButtonClick = () => setIsMapOpen(!isMapOpen);

  return (
    <>
      {heading}
      <Box css={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row', marginBottom: '600' }}>
        {address.street !== null && <ZestimateSummary />}
        {!isMobile && <DownloadPDFButton width={100} />}
      </Box>
      {comparables.items.length > 1 && (
        <RentRangeChart
          upperBound={getPriceOrZestimate(comparables.items[comparables.items.length - 1], userPropertyPrice)}
          lowerBound={getPriceOrZestimate(comparables.items[0], userPropertyPrice)}
          zestimate={userPropertyPrice}
        />
      )}
      <Box css={contentRecipe.raw({ isLoading })}>
        {comparables.items.length > 0 && (
          <Box css={actionsContainerStyles}>
            <FilterModule setIsLoading={setIsLoading} />
            {isMobile && (
              <>
                <Button
                  tone="brand"
                  emphasis="outlined"
                  onClick={handleMapOrListButtonClick}
                  icon={isMapOpen ? <IconChecklistEmptyFilled /> : <IconMapFilled />}
                  css={{ marginLeft: '200' }}
                >
                  {isMapOpen ? 'List' : 'Map'}
                </Button>
                <DownloadPDFButton />
              </>
            )}
          </Box>
        )}
        {isMapOpen && (
          <Box css={spacerRecipe.raw({ ignorePadding: isMobile })}>
            {/* oxlint-disable-next-line constellation/no-raw-spacing-values -- `height: 320px` is past the end of the numeric sizes scale, which stops at `5000` (200px) */}
            <Box css={{ position: 'relative', height: '320px', width: 'auto' }}>
              {areAppValuesSet && <Map useBubble={!isMobile} />}
            </Box>
            {!!activeComp && isListingCardOpen && <ListingCard listing={activeComp} />}
          </Box>
        )}
        <CompsCarousel isShowing={isCompsCarouselOpen} />
      </Box>
      <ChangePriceModal />
    </>
  );
};

export default PriceMyRentalComponent;
